import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  TaxOptionSchema,
  TaxOptionsListResponseSchema,
  type CreateTaxOptionRequest,
  type TaxOption,
  type UpdateTaxOptionRequest,
} from '@repo/api-client/dtos';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { deleteJSON, getJSON, patchJSON, postJSON } from '../internal/http';

const OkSchema = z.object({ ok: z.boolean() }).passthrough();

type QueryEnableOptions = { enabled?: boolean };

export function useTaxOptions(
  overrideOrgId?: string,
  options: QueryEnableOptions = {},
) {
  const http = useHttpClient();
  const activeOrgId = useActiveOrganizationId();
  const orgId = overrideOrgId ?? activeOrgId;
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data,
    refetch,
  } = useQuery({
    queryKey: orgId ? queryKeys.taxOptions.byOrg(orgId) : ['tax-options', 'idle'],
    queryFn: () =>
      getJSON(http, `/orgs/${orgId}/tax-options`, TaxOptionsListResponseSchema),
    enabled: enabled && !!orgId,
  });

  return {
    taxOptions: data as TaxOption[] | undefined,
    loading,
    error,
    refetch,
  };
}

export function useTaxOption(
  taxOptionId: string,
  overrideOrgId?: string,
  options: QueryEnableOptions = {},
) {
  const http = useHttpClient();
  const activeOrgId = useActiveOrganizationId();
  const orgId = overrideOrgId ?? activeOrgId;
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data: taxOption,
  } = useQuery({
    queryKey:
      orgId && taxOptionId
        ? queryKeys.taxOptions.detail(orgId, taxOptionId)
        : ['tax-options', 'idle'],
    queryFn: () =>
      getJSON(http, `/orgs/${orgId}/tax-options/${taxOptionId}`, TaxOptionSchema),
    enabled: enabled && !!orgId && !!taxOptionId,
  });

  return { taxOption: taxOption as TaxOption | undefined, loading, error };
}

export function useCreateTaxOption() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: (body: CreateTaxOptionRequest) => {
      if (!orgId) throw new Error('No active organization');
      return postJSON(http, `/orgs/${orgId}/tax-options`, body, TaxOptionSchema);
    },
    onSuccess: (taxOption) => {
      queryClient.setQueryData(
        queryKeys.taxOptions.detail(taxOption.orgId, taxOption.id),
        taxOption,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.taxOptions.byOrg(taxOption.orgId),
      });
    },
  });

  return {
    createTaxOption: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

export function useUpdateTaxOption() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: ({
      taxOptionId,
      updates,
    }: {
      taxOptionId: string;
      updates: UpdateTaxOptionRequest;
    }) => {
      if (!orgId) throw new Error('No active organization');
      return patchJSON(
        http,
        `/orgs/${orgId}/tax-options/${taxOptionId}`,
        updates,
        TaxOptionSchema,
      );
    },
    onSuccess: (taxOption) => {
      queryClient.setQueryData(
        queryKeys.taxOptions.detail(taxOption.orgId, taxOption.id),
        taxOption,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.taxOptions.byOrg(taxOption.orgId),
      });
    },
  });

  return {
    updateTaxOption: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

export function useDeleteTaxOption() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: async (taxOptionId: string) => {
      if (!orgId) throw new Error('No active organization');
      await deleteJSON(http, `/orgs/${orgId}/tax-options/${taxOptionId}`, OkSchema);
      return taxOptionId;
    },
    onSuccess: (taxOptionId) => {
      if (!orgId) return;
      queryClient.removeQueries({
        queryKey: queryKeys.taxOptions.detail(orgId, taxOptionId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.taxOptions.byOrg(orgId) });
    },
  });

  return {
    deleteTaxOption: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
